import React, { useEffect, useState } from "react";

import instance from "@store/middleware/api";

import { MobileMenuLogo } from "@/assets/svg";

/** Full-screen "we'll be back" page shown to all visitors while the site-wide
    maintenance flag is on (toggled from the admin Settings → General tab). */
const MaintenancePage: React.FC = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      background: `
        radial-gradient(ellipse 45% 40% at 42% -3%, rgba(176, 144, 105, 0.9) 0%, rgba(160, 128, 90, 0.5) 35%, transparent 75%),
        radial-gradient(ellipse 55% 50% at 58% 3%, rgba(170, 140, 100, 0.75) 0%, rgba(140, 110, 78, 0.35) 45%, transparent 85%),
        radial-gradient(ellipse 70% 35% at 50% 8%, rgba(150, 118, 82, 0.4) 0%, transparent 70%),
        linear-gradient(180deg, rgb(153, 112, 75) 0%, rgb(99, 74, 54) 50%, rgb(111, 83, 60) 100%)`,
      color: "#fbf4ec",
      padding: "24px",
      textAlign: "center",
    }}
  >
    <MobileMenuLogo style={{ height: "56px", width: "auto", marginBottom: "18px" }} />
    <div style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "2.25rem", color: "#fbf4ec", marginBottom: "14px", letterSpacing: "0.02em" }}>
      Audio Sanctum
    </div>
    <div style={{ width: "210px", height: "1px", background: "linear-gradient(90deg, transparent, rgba(255, 220, 168, 0.85), transparent)", marginBottom: "24px" }} />
    <h1 style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "1.75rem", fontWeight: 500, color: "#ffdca8", margin: "0 0 2px" }}>
      We&rsquo;ll be right back
    </h1>
    <p style={{ maxWidth: 440, lineHeight: 1.65, color: "rgba(251,244,236,0.72)", fontSize: "0.95rem", fontFamily: '"Raleway", sans-serif' }}>
      We are undergoing scheduled maintenance and making things better for you
      right now. Audio Sanctum will be back shortly &mdash; thank you for your patience.
    </p>
  </div>
);

/** Checks the site-wide maintenance flag once on load. Fails OPEN — a slow or
    failing check never blocks the site (1.5s timeout + catch both render the
    normal app), so the gate can only ever *add* a maintenance page, never hide
    a working site by accident. */
export const MaintenanceGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<"checking" | "ok" | "maintenance">("checking");

  useEffect(() => {
    let done = false;
    const finish = (s: "ok" | "maintenance") => { if (!done) { done = true; setStatus(s); } };
    instance
      .get("/subscription-config/maintenance")
      .then((r) => finish(r.data?.maintenance ? "maintenance" : "ok"))
      .catch(() => finish("ok"));
    const t = setTimeout(() => finish("ok"), 1500); // fail-open if the check hangs
    return () => clearTimeout(t);
  }, []);

  if (status === "checking") return null; // brief; the check is a single fast GET
  if (status === "maintenance") return <MaintenancePage />;
  return <>{children}</>;
};

export default MaintenanceGate;
